import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { SearchBox, Option } from './SearchBox';
import { TextButton } from 'js/design_system/TextButton';
import { useToast } from 'js/design_system/toasts/hooks';
import {
  KiiData,
  KiiSource,
  postKiiData,
  postRawInputData,
  RawInputData,
  KiiResponse,
} from './api/KiiDataApi';
import styled from 'astroturf/react';

const DEFAULT_HEIGHT = 505;

const SearchContainer = styled.div`
  align-self: stretch;
`;

export type InputOption = {
  inputBoxId: number;
  option: Option;
};

export enum SearchType {
  CoauthorConstrained = 'coauthor_constrained',
  PaperTitleConstrained = 'paper_title_constrained',
  PaperTitleAll = 'paper_title_all',
  AuthorClaiming = 'author_claiming',
}

type KiiTypeaheadProps = {
  label?: string;
  placeholderText?: string;
  emptyErrorText: string;
  searchType: SearchType;
  height?: number;
  dropdownHeightOffset?: number;
  acceptMultipleInput?: boolean;
  addInputText?: string;
  maxInputsAllowed?: number;
  onChange?: (option: InputOption) => void;
  onSelect?: (option: InputOption) => void;
};

// Define the interface for the methods that the component will expose
export interface KiiTypeaheadHandle {
  submit: (
    source: KiiSource,
    submitted?: boolean,
    showToast?: boolean,
  ) => Promise<{
    error: boolean;
    result?: KiiResponse;
  }>;
}

/**
 * # KiiTypeahead
 * Component used for capturing KII data via Typeahead. KII types supported include co-authors and paper titles, which will be sourced
 * using OpenAlex.
 *
 * #### Props
 * - `label`: The input's label. Shown at the top of the component.
 * - `placeholderText`: Text to show in the input box as a placeholder before text is enterered
 * - `emptyErrorText`: The text to show when trying to submit without input.
 * - `height`: The height of the component including the dropdown.
 * - `emptyErrorText`: The text to show when trying to submit without input.
 * - `searchType`: The type of item being searched.
 * - `acceptMultipleInput`: Whether to render multiple typeaheads.
 * - `addInputText`: When `acceptMultipleInput` is true, represents the text prompting the user to add another input box.
 * - `maxInputsAllowed`: The max amount of inputs to allow to be rendered
 * - `onChange`: Callback to invoke when the input value changes.
 * - `onSelect`: Callback to invoke when selecting an option.
 *
 * #### Methods
 *
 * The `KiiTypeahead` provides methods via a ref to control and manage the component externally.
 * - `submit`: Triggers the submit action within the component. Use this method to programmatically submit the KiiTypeahead input(s). Passing in false signifies that nothing was actually submitted
 *
 * ## Basic Usage
 *
 * @example
 * ```
 * import { useRef } from 'react';
 * import { KiiTypeahead, KiiTypeaheadHandle, SearchType } from 'js/kii_component';
 * const label = 'Coauthor name';
 * const emptyErrorText = 'Please add a coauthor';
 * const searchType = SearchType.CoauthorConstrained;
 * const acceptMultipleInput = true;
 * const addInputText = 'Add another coauthor';
 *
 * const kiiTypeaheadRef = useRef<KiiTypeaheadHandle>(null);
 *
 * <KiiTypeahead
 *  ref={kiiTypeaheadRef}
 *  label={searchHeader}
 *  emptyErrorText={emptyError}
 *  searchType={searchType}
 *  acceptMultipleInput={acceptMultipleInput}
 *  addInputText={addInputText}
 * />
 *
 * // Accessing methods via the ref
 * kiiTypeaheadRef.current?.submit();
 * ```
 */

export const KiiTypeahead = React.forwardRef<
  KiiTypeaheadHandle,
  KiiTypeaheadProps
>(
  (
    {
      label: heading,
      placeholderText,
      emptyErrorText,
      searchType,
      height,
      dropdownHeightOffset,
      acceptMultipleInput,
      maxInputsAllowed,
      addInputText,
      onChange,
      onSelect,
    },
    ref,
  ) => {
    const { toast } = useToast();
    const [inputIds, setInputIds] = useState<number[]>([]);
    const inputsDataRef = useRef<{ [key: number]: Option | undefined }>({});
    const inputRawDataLoggerRef = useRef<{ [key: number]: string[] }>({});
    const [error, setError] = useState<boolean>(false);

    const maxInputCount = maxInputsAllowed ? maxInputsAllowed : 3;
    const addText = addInputText ? addInputText : 'Add another';

    useEffect(() => addInput(), []);

    const addInput = () => {
      setInputIds((prevInputIds) => {
        const newId =
          prevInputIds.length > 0 ? Math.max(...prevInputIds) + 1 : 1;

        inputsDataRef.current[newId] = undefined;
        inputRawDataLoggerRef.current[newId] = [];

        return [...prevInputIds, newId];
      });
    };

    const removeInput = (id: number) => {
      setInputIds((prevInputIds) =>
        prevInputIds.filter((inputId) => inputId !== id),
      );

      delete inputsDataRef.current[id];
      delete inputRawDataLoggerRef.current[id];
    };

    const handleInputChange = useCallback(
      (newOption: Option, id: number) => {
        if (error) setError(false);

        inputsDataRef.current[id] = newOption;
        inputRawDataLoggerRef.current[id].push(newOption.label);

        if (onChange) onChange({ inputBoxId: id, option: newOption });
      },
      [error, onChange],
    );

    const handleSelectionChange = useCallback(
      (newOption: Option, id: number) => {
        if (error) setError(false);

        inputsDataRef.current[id] = newOption;
        inputRawDataLoggerRef.current[id].push(newOption.label);

        if (onSelect) onSelect({ inputBoxId: id, option: newOption });
      },
      [onSelect, error],
    );

    const onSubmit = async (
      source: KiiSource,
      submitted: boolean | undefined,
      showToast = true,
    ) => {
      let hasError = false;

      if (submitted || submitted === undefined) {
        const validKiiData: KiiData[] = [];

        inputIds.forEach((id) => {
          const input = inputsDataRef.current[id];
          if (input !== undefined && input?.label !== '') {
            validKiiData.push({
              option: input,
              rawData: inputRawDataLoggerRef.current[id],
            });
          }
        });

        if (validKiiData.length > 0) {
          if (showToast) {
            toast({
              text: '✓ Thank you for your submission',
              placement: 'top',
            });
          }

          try {
            const result = await postKiiData(searchType, validKiiData, source);
            return {
              error: false,
              result,
            };
          } catch {
            return {
              error: true,
            };
          }
        } else {
          for (let i = 1; i < inputIds.length; i++) {
            removeInput(inputIds[i]);
          }

          hasError = true;
          setError(hasError);
        }
      } else {
        const validData: RawInputData[] = [];

        inputIds.forEach((id) => {
          const input = inputRawDataLoggerRef.current[id];
          if (input.length > 0) {
            validData.push({ input });
          }
        });

        if (validData.length > 0) {
          try {
            await postRawInputData(validData, source);
            return {
              error: false,
            };
          } catch {
            return {
              error: true,
            };
          }
        }
      }

      return {
        error: hasError,
      };
    };

    useImperativeHandle(ref, () => ({
      submit: onSubmit,
    }));

    return (
      <SearchContainer style={{ height: `${height ?? DEFAULT_HEIGHT}px` }}>
        {inputIds.map((id) => (
          <SearchBox
            key={id}
            heading={heading}
            placeholderText={
              placeholderText ?? 'Add a few words to see auto-suggestions...'
            }
            height={height ?? DEFAULT_HEIGHT}
            dropdownHeightOffset={dropdownHeightOffset}
            emptyErrorText={emptyErrorText}
            searchType={searchType}
            error={error}
            onChange={(input) => handleInputChange(input, id)}
            onSelect={(selection) => handleSelectionChange(selection, id)}
          />
        ))}
        {acceptMultipleInput && inputIds.length < maxInputCount ? (
          <TextButton icon={'add'} inline={true} onClick={addInput}>
            {addText}
          </TextButton>
        ) : null}
      </SearchContainer>
    );
  },
);
